import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

/**
 * Interceptor de autenticación.
 * - Agrega el header Authorization con el JWT si existe y no expiró.
 * - En caso de respuesta 401, cierra la sesión y redirige al login
 *   mediante el Router (sin location.reload(), que causaba loops infinitos).
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const conToken = (r: HttpRequest<unknown>, token: string) =>
        token ? r.clone({ headers: r.headers.set('Authorization', 'Bearer ' + token) }) : r;

    // Inyectar el token si existe
    const tokenEnviado = authService.accessToken;
    const newReq = conToken(req, tokenEnviado);

    // Las rutas de sesión no se renuevan a sí mismas (login, refresh, logout, social).
    const ruta = req.url.split('?')[0];
    const sinRenovar = /\/auth\/(login|refresh|logout|social)$/.test(ruta);
    // POST /users/verify-email responde 401 cuando la contraseña o la cuenta
    // social no coinciden (SOCIAL_IDENTIDAD_NO_COINCIDE): es un error de la
    // pantalla, no la sesión. Ni se renueva (contaría otro intento) ni se cierra.
    const esVerificarCorreo = /\/users\/verify-email/.test(ruta);
    // El 401 de /auth/refresh lo resuelve AuthService.renovarSesion (relee el
    // almacenamiento por si otra pestaña ya rotó el refresh): aquí no se
    // cierra la sesión, o borraría el par que la otra pestaña acaba de dejar.
    const esRefresh = /\/auth\/refresh$/.test(ruta);

    // Manejar respuestas de error
    return next(newReq).pipe(
        catchError((error) => {
            // El 401 de un token de Google/Apple/Facebook rechazado (entrar o
            // vincular) no es la sesión vencida: lo maneja la pantalla.
            const tokenSocialRechazado =
                error instanceof HttpErrorResponse && error.error?.error === 'SOCIAL_TOKEN_INVALIDO';

            // 401 de negocio: se devuelve tal cual al componente.
            if (error instanceof HttpErrorResponse && error.status === 401 && (tokenSocialRechazado || esVerificarCorreo)) {
                return throwError(() => error);
            }

            // 401 con refresh token: se renueva la sesión una vez y se repite
            // la petición (como la app). Solo si falla se cierra la sesión.
            if (
                error instanceof HttpErrorResponse &&
                error.status === 401 &&
                !sinRenovar &&
                authService.refreshToken
            ) {
                return authService.renovarSesion(tokenEnviado).pipe(
                    switchMap((token) => next(conToken(req, token))),
                    catchError((err2) => {
                        if (!(err2 instanceof HttpErrorResponse) || err2.status === 401) {
                            authService.signOut();
                            router.navigate(['auth/sign-in']);
                        }
                        return throwError(() => (err2 instanceof HttpErrorResponse ? err2 : error));
                    }),
                );
            }

            // Solo en 401: cerrar sesión y redirigir al login
            // NOTA: usamos router.navigate en lugar de location.reload()
            // para evitar el loop infinito que ocurría cuando la página
            // se recargaba sin token pero seguía montándose y haciendo
            // peticiones que volvían a recibir 401.
            if (error instanceof HttpErrorResponse && error.status === 401 && !esRefresh) {
                authService.signOut();
                router.navigate(['auth/sign-in']);
            }

            return throwError(error);
        })
    );
};
